import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchShipmentsByEmail } from "../store/features/shipmentsSlice";
import CaseHeader from "../components/Header/CaseHeader"
import StatusCard from "../components/Header/StatusCard"
import RequiredActions from "../components/Dashboard/RequiredAction";
import TabSection from "../components/Dashboard/TabSection";


const CaseDetail = () => {
  const { shipmentId } = useParams(); // Access the dynamic part of the URL
  const dispatch = useDispatch();
  const email = "supplychain@linear-works.com";
  const [isDataLoaded, setIsDataLoaded] = useState(false); // Track if data is loaded
  const { shipments, status } = useSelector((state) => state.shipments);

  // Fetch all shipments on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchShipmentsByEmail(email)); // Await the dispatch
        setIsDataLoaded(true); // Mark data as loaded
      } catch (error) {
        console.error("Failed to fetch shipments:", error);
      }
    };

    fetchData(); // Call the async function
  }, [dispatch, email]); // Removed `shipments` from dependencies

  // Find the shipment by shipmentId
  const caseData = shipments.find((shipment) => shipment.shipment_id === shipmentId);
  
  // Handle loading and error states
  if (status === "loading") return <div>Fetching Latest information please hold on</div>;
  if (status === "failed") return <div>Failed to load case data.</div>;
  // If caseData is not found, show an error or a fallback UI
  if (!caseData) return <div>Case not found.</div>;
  // Destructure caseData only if it exists
  const { thread_id, case_number, documents, emailBodies } = caseData;

  // Get the last document
  const lastDocument = documents.reduce((maxDoc, currentDoc) => {
    // Check if mandatory_info or MandatoryInfo is not an empty object
    const hasMandatoryInfo =
      (currentDoc.mandatory_info && Object.keys(currentDoc.mandatory_info).length > 0) ||
      (currentDoc.MandatoryInfo && Object.keys(currentDoc.MandatoryInfo).length > 0);
  
    // Only consider documents with non-empty mandatory_info or MandatoryInfo
    if (hasMandatoryInfo) {
      return currentDoc.id > maxDoc.id ? currentDoc : maxDoc;
    }
    return maxDoc; // Keep the current maxDoc if the condition is not met
  }, documents[0]);

  // Safely access mandatoryInfo with fallbacks
  const mandatoryInfo = lastDocument.content?.MandatoryInfo || lastDocument.content?.mandatory_info || {};

  // Extract the required values with error handling
  const origin = mandatoryInfo?.Origin?.PortOfDespatchArrival?.Value || "N/A"; // Origin (Port of Departure/Arrival)
  const destination = mandatoryInfo?.DeliveryPoint?.CompanyNameAddress?.Value || "N/A"; // Destination (Company Name and Address)
  const destcountry = destination.split(",").pop()?.trim() || "N/A"; // Extract country from destination
  const carrier = mandatoryInfo?.DeliveryPoint?.ContactName?.Value || "N/A"; // Carrier (Ship Name/Flight Number)
  const awb = mandatoryInfo?.Origin?.["Airwaybill Number"]?.Value || "N/A"; // AWB (Airwaybill Number)

  // Find the earliest email
  const earliestEmail = emailBodies.reduce((earliest, email) => {
    const currentTimestamp = new Date(email.timestamp);
    const earliestTimestamp = new Date(earliest.timestamp);
    return currentTimestamp < earliestTimestamp ? email : earliest;
  });

  // Format the earliest timestamp to "date-month-year"
  const formatDate = (dateString, daysToAdd = 0) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + daysToAdd); // Add the specified number of days

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const eta = formatDate(earliestEmail.timestamp, 10); // eta is currently default adding 10
  const date = formatDate(earliestEmail.timestamp);
  const ship_date = mandatoryInfo?.Origin?.["ArrivalDate"]?.Value || "N/A";

  const notDelivered = !caseData.delivered || caseData.delivered.trim() === "";

  // Format the caseData for rendering
  const formattedStatus = [
    { label: "Initiated", date: date },
    { label: "Shipped", date: ship_date || "In transit" },
    { label: "Issue", hasIssue: !!caseData.issue }, // Boolean value for issue status
    { label: "ETA", date: eta },
  ];

  



  return (
    <div className="px-3">
      {/* CaseHeader */}
      <CaseHeader
        caseId={case_number}
        shipmentId={thread_id}
        origin={origin}
        destination={destination}
        carrier={carrier}
        trackingNumber={awb}
      />

      {/* Status Cards */}
      <div className="grid grid-cols-4 gap-8 my-6">
        {formattedStatus.map((status, index) => (
          <StatusCard
            key={index}
            label={status.label}  // Display the status label (e.g., "Initiated")
            date={status.date}    // Optional date for statuses with a timestamp
            hasIssue={status.hasIssue}  // Boolean value for "Issue"
            delivered={status.delivered}
          />
        ))}
      </div>

      {/* Required Actions */}
      <RequiredActions threadId={thread_id}  />

      {/* Tab Section */}
      <div className="pt-6">
        <TabSection threadId={thread_id} />
      </div>
      
    </div>
  );
};

export default CaseDetail;